import { Button, createStyles, Grid, MultiSelect, Select, Table, TextInput, NumberInput, SelectItem, Group, ScrollArea, Flex } from "@mantine/core";
import { ReactNode, useImperativeHandle, useRef, useState } from "react";
import { DependentOption, InputElement, InputType, Keys, QueryKeys } from "../../models/models";
import { useQuery, useStore } from "../../stores/store";
import t, { wrappers } from "../../stores/language";
import { observer } from "mobx-react";
import Modal from "../../custom-components/model";
import React from "react";
import { toJS } from "mobx";
import { useParams } from "react-router-dom";
import { Icon } from "../../custom-components/icons";
import { faPencil, faTrash, faPlus } from "@fortawesome/pro-duotone-svg-icons";
import { TableView, useTableStyles } from "../../custom-components/table";
import theme from "../../stores/theme";
import PopConfirm from "../../custom-components/pop-confirm";

export const useStyles = createStyles((theme) => ({
  inputContainer: {
    padding: `${theme.other.arc.paddings[0]} ${theme.other.arc.paddings[2]}`,
    actionContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    margin: `${theme.other.arc.margins[0]}`,
  },
  title: {
    padding: `${theme.other.arc.paddings[1]} ${theme.other.arc.paddings[0]}`,
    fontWeight: 500,
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.other.arc.margins[0],
    fontSize: theme.fontSizes.lg,
  },
  gridAddButton: {
    display: "flex",
    paddingTop: theme.other.arc.paddings[4],
    alignItems: "center",
    ".gridAddIcon": {
      padding: theme.other.arc.paddings[4],
    },
  },
  addIconButton: {
    color: theme.other.arc.colors[2],
    backgroundColor: "transparent",
    padding: theme.other.arc.paddings[1],
    "&: not([data-disabled]):hover": {
      backgroundColor: "transparent",
    },
  },
  iconButton: {
    "&: hover": {
      backgroundColor: "transparent",
    },
  },
  // configure-formula uses same class in addition following gridColumn & footer class
  //module-creator uses footer class
  gridColumn: {
    display: "flex",
    alignItems: "end",
    paddingBottom: theme.other.arc.paddings[2],
  },
  modalFooter: {
    borderTop: "1px solid #ccc",
    padding: theme.other.arc.paddings[2],
    justifyContent: "end",
  },
  // result-view uses same class in addition following container, part & partHeader class
  // validate-formula uses following container, part & partHeader class
  container: {
    height: "inherit",
    maxHeight: "inherit",
    overflow: "inherit",
    margin: theme.other.arc.margins[0],
  },
  parts: {
    boxShadow: theme.shadows.lg,
    maxHeight: "inherit",
    padding: theme.other.arc.paddings[0],
  },
  partHeader: {
    display: "flex",
    fontSize: theme.fontSizes.lg,
  },
  configureModal: {
    ".mantine-Modal-body": {
      height: "calc(100vh - 142px)",
      display: "flex",
      flexWrap: "nowrap",
      flexDirection: "column",
      justifyContent: "space-between",
    },
  },
  reportsContainer: {
    boxShadow: theme.other.arc.shadows.lg,
    padding: theme.other.arc.paddings[2],
    flex: 1,
    height: "100%",
  },
}));
export const ConfigureInput = observer(function ConfigureInput() {
  const { store } = useStore();
  const { classes } = useStyles();
  const inputCreateRef = useRef<InputCreateHandle>(null);
  const { id } = useParams();
  const deleteInput = (id: number) => {
    store.DeleteInputData(id);
  };

  const inputElements = store.GetInputByModule(id ?? "");
  const inputElementsOrder = store.GetModuleById(id ?? "0")?.moduleConfig?.inputElementsOrder;

  const data = inputElements.map((s) => {
    const row: { [key: string]: string | null | number } = {};
    row.id = s.id.toString();
    row.name = s.name;
    row.type = s.type.toString();
    row.default = s.default;
    row.group = s.inputConfig?.group ?? "";
    return row;
  });

  const GetActions = (row: { [key: string]: string | null | number }) => {
    return (
      <Flex>
        <Button
          size="xs"
          onClick={() => {
            inputCreateRef.current?.show(row.id as number);
          }}
          variant="subtle"
          compact
          className={classes.iconButton}
        >
          <Icon icon={faPencil} size="xl" />
        </Button>
        <PopConfirm confirmText={t("Are You Sure?")} width={180} onConfirm={() => deleteInput(row.id as number)}>
          <Button size="xs" variant="subtle" compact className={classes.iconButton}>
            <Icon icon={faTrash} size="xl" color={"red"} />
          </Button>
        </PopConfirm>
      </Flex>
    );
  };

  const reOrder = (orderedData: number[]) => {
    store.UpdateSortOrder(parseInt(id ?? "0"), "InputElements", orderedData);
  };

  return (
    <>
      <div className={classes.title}>
        <span>{t("Added Input Elements")} </span>
        <Button size="xs" onClick={() => inputCreateRef.current?.show(0)} className={classes.addIconButton}>
          {<Icon size="2x" icon={faPlus}></Icon>}
        </Button>
      </div>
      <TableView
        data={data}
        columns={[
          { key: "group", label: "Group", width: "13%" },
          { key: "name", label: "Name", width: "35%" },
          { key: "type", label: "Type", width: "25%" },
          { key: "default", label: "Default", width: "27%" },
        ]}
        actions={GetActions}
        reOrder={reOrder}
        order={inputElementsOrder}
      />
      <InputCreator ref={inputCreateRef}></InputCreator>
    </>
  );
});

// crete input element component

interface InputCreateHandle {
  show: (input: number) => void;
}
const defaultInput: InputElement = {
  id: 0,
  name: "",
  type: "Number Input",
  default: "0",
};

const InputCreator = observer(
  React.forwardRef<InputCreateHandle, unknown>(function InputCreator(props, ref) {
    const { classes, cx } = useStyles();
    const tableClass = useTableStyles();
    const { store } = useStore();
    const [visible, setVisible] = useState<boolean>(false);
    const { id } = useParams();
    const inputElements = store.GetInputByModule(id ?? "");
    useImperativeHandle(ref, () => {
      return {
        show: show,
      };
    });
    const [inputElement, setInputElement] = useState<InputElement>(defaultInput);
    const { data: lookupDataPageNo } = useQuery({
      queryKey: [QueryKeys.LookupDataPageNo],
      queryFn: async () => {
        return await store.GetAllLookupDataPageNo();
      },
    });

    const show = (id: number) => {
      const input = store.inputElements.find((s) => s.id == id);
      if (!input) {
        setInputElement(defaultInput);
      } else {
        setInputElement(input);
      }
      setVisible(true);
    };

    const resetState = () => {
      setInputElement(defaultInput);
    };

    const addInput = () => {
      const elementToAdd = inputElement;

      if (elementToAdd.id === 0) {
        store.AddInputData({ ...elementToAdd, id: inputElements.length + 1, moduleId: Number(id ?? "0") });
      } else {
        store.UpdateInputData({
          ...elementToAdd,
          inputConfig: { ...elementToAdd.inputConfig, displayName: inputElement.inputConfig?.displayName ?? elementToAdd.name },
        });
      }

      setVisible(false);
      resetState();
    };

    // To Get Available Group Names as Options in Group Dropdown
    const getInputGroups = () => {
      const inputs = inputElements.filter((input) => input.inputConfig?.group !== "");
      if (inputElement.inputConfig?.group !== "") inputs.push(inputElement);
      const groups = [...new Set(inputs.map((input) => input.inputConfig?.group ?? ""))].filter((i) => i !== "");
      if (!groups.includes("Comments")) {
        groups.push("Comments");
      }
      return groups;
    };

    const updateDependentOptions = (dependentColumns: string[]) => {
      const dependentInputElements = inputElements.filter((d) => dependentColumns?.includes(d.id.toString()));
      const columnNames = dependentInputElements.map((d) => d.name);
      const corssJoinMapping = crossJoin(getDependentOptionsData(dependentInputElements));
      const dependentOptions: DependentOption[] = [];
      corssJoinMapping.map((r) => {
        const keys: Keys[] = [];
        r.forEach((c, ix) => {
          keys.push({ column: columnNames[ix], value: c });
        });
        dependentOptions.push(setDependentOptions(keys, [...GetDependentOptions(keys, inputElement)], [])[0]);
      });
      return dependentOptions;
    };

    const getDependentOptionsData = (dependentInputElements: InputElement[]) => {
      const dependentOptionsData = dependentInputElements.map((d) => {
        if (d.type === "Dependent Dropdown") {
          const dependentOptions: string[] = (d.inputConfig?.dependentOptions ?? []).flatMap((dependentOption) => {
            // Value is wrapped inside [] to differentiate dependent dropdown options
            const dependsOn = dependentOption.keys.map((k) => (k.value.includes("[") ? k.value : `[${k.value}]`)).join("");
            return dependentOption.options.map((option) => dependsOn.concat(`[${option}]`));
          });
          return dependentOptions;
        } else {
          return d.inputConfig?.options ?? [];
        }
      });
      return dependentOptionsData;
    };

    const getDependentTable = () => {
      const dependentInputElements = inputElements.filter((d) => inputElement.inputConfig?.dependentColumn?.includes(d.id.toString()));
      const headers = dependentInputElements.map((d) => d.name);
      headers.push("Options");
      const rowMetaData = getDependentOptionsData(dependentInputElements);
      const corssJoinMapping = crossJoin(rowMetaData);
      const rows = corssJoinMapping.map((r, i) => {
        const row: ReactNode[] = [];
        const keys: Keys[] = [];
        r.forEach((c, ix) => {
          keys.push({ column: headers[ix], value: c });
          row.push(<td key={ix}>{c}</td>);
        });
        row.push(
          <td key={"option"}>
            <MultiSelect
              creatable
              searchable
              withinPortal
              label={null}
              getCreateLabel={(query) => `+ Add : ${query}`}
              data={[...GetDependentOptions(keys, inputElement)]}
              onCreate={(query) => {
                const item = { value: query, label: query };
                setInputElement({
                  ...inputElement,
                  inputConfig: {
                    ...inputElement.inputConfig,
                    dependentOptions: setDependentOptions(
                      keys,
                      [...GetDependentOptions(keys, inputElement), query],
                      inputElement.inputConfig?.dependentOptions ?? []
                    ),
                  },
                });
                return item;
              }}
              value={GetDependentOptions(keys, inputElement)}
              onChange={(e) =>
                setInputElement({
                  ...inputElement,
                  inputConfig: {
                    ...inputElement.inputConfig,
                    dependentOptions: setDependentOptions(keys, e, inputElement.inputConfig?.dependentOptions ?? []),
                  },
                })
              }
            />
          </td>
        );
        return <tr key={i}>{row}</tr>;
      });
      //todo
      return (
        <ScrollArea style={{ height: "calc(100vh - 464px)" }}>
          <Table sx={{ minWidth: 800 }} verticalSpacing="xs" withBorder withColumnBorders>
            <thead className={cx(tableClass.classes.header, { [tableClass.classes.scrolled]: false })}>
              <tr>
                {headers.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </Table>
        </ScrollArea>
      );
    };
    const dependents = () => {
      switch (inputElement.type) {
        case "Dependent Dropdown":
          return (
            <>
              <Grid.Col span={12}>
                <MultiSelect
                  label={t("Dependends On")}
                  value={inputElement.inputConfig?.dependentColumn}
                  data={inputElements
                    .filter((d) => d.type === "Dropdown" || (d.type === "Dependent Dropdown" && d.id != inputElement.id))
                    .map((d) => {
                      return { value: d.id.toString(), label: d.name } as SelectItem;
                    })}
                  onChange={(v) => {
                    setInputElement({
                      ...inputElement,
                      inputConfig: { ...inputElement.inputConfig, dependentColumn: v ?? null, dependentOptions: updateDependentOptions(v) },
                    });
                  }}
                />
              </Grid.Col>
              {inputElement.inputConfig?.dependentColumn ? <Grid.Col span={12}>{getDependentTable()}</Grid.Col> : null}
            </>
          );
        case "Dropdown":
          return (
            <>
              <Grid.Col span={6}>
                <MultiSelect
                  creatable
                  searchable
                  label={t("Options")}
                  getCreateLabel={(query) => `+ Add : ${query}`}
                  data={[...(inputElement.inputConfig?.options ?? [])]}
                  onCreate={(query) => {
                    const item = { value: query, label: query };
                    setInputElement({
                      ...inputElement,
                      inputConfig: { ...inputElement.inputConfig, options: [...(inputElement.inputConfig?.options ?? []), query] },
                    });
                    return item;
                  }}
                  value={inputElement.inputConfig?.options}
                  onChange={(e) => setInputElement({ ...inputElement, inputConfig: { ...inputElement.inputConfig, options: e } })}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label={t("Default")} value={inputElement.default} onChange={(e) => setInputElement({ ...inputElement, default: e.target.value })} />
              </Grid.Col>
            </>
          );
        case "Number Input":
          return (
            <>
              <Grid.Col span={4}>
                <NumberInput
                  label={t("Minimum")}
                  value={inputElement.inputConfig?.min}
                  onChange={(e) => setInputElement({ ...inputElement, inputConfig: { ...inputElement.inputConfig, min: Number(e) } })}
                  precision={theme.other?.numberInput.precision}
                  step={theme.other?.numberInput.step}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <NumberInput
                  label={t("Maximum")}
                  value={inputElement.inputConfig?.max}
                  onChange={(e) => setInputElement({ ...inputElement, inputConfig: { ...inputElement.inputConfig, max: Number(e) } })}
                  precision={theme.other?.numberInput.precision}
                  step={theme.other?.numberInput.step}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput label={t("Default")} value={inputElement.default} onChange={(e) => setInputElement({ ...inputElement, default: e.target.value })} />
              </Grid.Col>
            </>
          );
        case "Radio":
          return (
            <>
              <Grid.Col span={6}>
                <MultiSelect
                  creatable
                  searchable
                  label={t("Options")}
                  getCreateLabel={(query) => `+ Add : ${query}`}
                  data={[...(inputElement.inputConfig?.options ?? [])]}
                  onCreate={(query) => {
                    const item = { value: query, label: query };
                    setInputElement({
                      ...inputElement,
                      inputConfig: { ...inputElement.inputConfig, options: [...(inputElement.inputConfig?.options ?? []), query] },
                    });
                    return item;
                  }}
                  value={inputElement.inputConfig?.options}
                  onChange={(e) => setInputElement({ ...inputElement, inputConfig: { ...inputElement.inputConfig, options: e } })}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label={t("Default")} value={inputElement.default} onChange={(e) => setInputElement({ ...inputElement, default: e.target.value })} />
              </Grid.Col>
            </>
          );
        case "Year Input":
          return (
            <>
              <Grid.Col span={4}>
                <Select
                  onChange={(value) => {
                    setInputElement({ ...inputElement, default: value ?? "2023" });
                  }}
                  value={inputElement.default}
                  data={lookupDataPageNo ? Object.keys(lookupDataPageNo).sort((a, b) => parseInt(a) - parseInt(b)) : []}
                  label={t("Default")}
                  searchable
                  nothingFound={t("No year found")}
                  maxDropdownHeight={150}
                />
              </Grid.Col>
            </>
          );
        case "Text Input":
          return (
            <Grid.Col span={4}>
              <TextInput label={t("Default")} value={inputElement.default} onChange={(e) => setInputElement({ ...inputElement, default: e.target.value })} />
            </Grid.Col>
          );
        default:
          return <></>;
      }
    };
    return (
      <>
        <Modal
          className={classes.configureModal}
          opened={visible}
          header={inputElement.id === 0 ? t("Add Input Element") : t(`Update ${inputElement.inputConfig?.displayName ?? inputElement.name}`)}
          footer={
            <Group className={classes.modalFooter}>
              <Button
                variant={"default"}
                onClick={() => {
                  setVisible(false);
                }}
              >
                {t("Cancel")}
              </Button>
              <Button onClick={() => addInput()}>{inputElement.id === 0 ? t("Add") : t("Update")}</Button>
            </Group>
          }
          withCloseButton={true}
          onClose={() => {
            setVisible(false);
            resetState();
          }}
          size={"80%"}
        >
          <Grid pb={16} columns={12} className={classes.inputContainer}>
            <Grid.Col span={6}>
              <TextInput
                label={t("Key")}
                value={inputElement.name ? `${wrappers.openingWrapper}${inputElement.name}${wrappers.closingWrapper}` : ""}
                disabled
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label={t("Display Name")}
                width={"100%"}
                value={inputElement.inputConfig?.displayName ?? inputElement.name}
                onChange={(e) => {
                  if (inputElement.id === 0) {
                    setInputElement({
                      ...inputElement,
                      name: e.target.value,
                      inputConfig: { ...inputElement.inputConfig, displayName: e.target.value ?? "" },
                    });
                  } else {
                    setInputElement({ ...inputElement, inputConfig: { ...inputElement.inputConfig, displayName: e.target.value ?? "" } });
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                label={t("Group")}
                data={getInputGroups()}
                creatable
                searchable
                clearable
                getCreateLabel={(query) => `+ Add ${query}`}
                onCreate={(query) => {
                  if (inputElement.type !== "Text Input") {
                    setInputElement({
                      ...inputElement,
                      inputConfig: { ...inputElement.inputConfig, group: query },
                    });
                  }
                  return query;
                }}
                value={inputElement.inputConfig?.group ?? ""}
                onChange={(e) => {
                  if (inputElement.type !== "Text Input") {
                    setInputElement({
                      ...inputElement,
                      inputConfig: { ...inputElement.inputConfig, group: e ?? "" },
                    });
                  }
                }}
                readOnly={inputElement.type === "Text Input"}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Select
                label={t("Type")}
                clearable={false}
                data={["Dropdown", "Dependent Dropdown", "Number Input", "Radio", "Year Input", "Text Input"] as InputType[]}
                value={inputElement.type}
                onChange={(value: InputType) => {
                  const newGroup = value === "Text Input" ? "Comments" : inputElement.inputConfig?.group;

                  setInputElement({
                    ...inputElement,
                    type: value,
                    inputConfig: {
                      ...inputElement.inputConfig,
                      group: newGroup,
                    },
                  });
                }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TextInput
                label={t("Input Help")}
                width={"100%"}
                value={inputElement.inputConfig?.inputHelp ?? ""}
                onChange={(e) => {
                  setInputElement({ ...inputElement, inputConfig: { ...inputElement.inputConfig, inputHelp: e.target.value ?? "" } });
                }}
              />
            </Grid.Col>
            {dependents()}
          </Grid>
        </Modal>
      </>
    );
  })
);
const isMatch = (keys: Keys[], key: Keys) => {
  return keys.find((d) => d.column === key.column && d.value === key.value) != null ? true : false;
};
export const GetDependentOptions = (keys: Keys[], inputElement: InputElement) => {
  const base = toJS(inputElement.inputConfig?.dependentOptions);
  if (base == null) {
    return [];
  }
  const dependent = base.find((d) => d.keys.every((d) => isMatch(keys, d)));
  if (dependent != null) {
    return dependent.options;
  }
  return [];
};
export const setDependentOptions = (keys: Keys[], options: string[], currentValue: DependentOption[]) => {
  const match = currentValue?.findIndex((d) => d.keys.every((d) => isMatch(keys, d)));
  if (match > -1) {
    currentValue[match].options = options;
  } else {
    currentValue.push({ keys: keys, options: options });
  }
  return currentValue;
};
function crossJoin(arr: string[][]) {
  return arr.reduce<string[][]>(
    function (a, b) {
      return a
        .map(function (x) {
          return b.map(function (y) {
            return x.concat([y]);
          });
        })
        .reduce(function (a, b) {
          return a.concat(b);
        }, []);
    },
    [[]]
  );
}
